import React, { useState, useEffect } from 'react';

import { apiRequest } from '@/src/services';
import type { UserInfo } from '@/src/app/context/useAuth';
import { useAuth } from '@/src/app/context/useAuth';
import { Constants } from '@/src/utils';

const LoginForm: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const { login } = useAuth();

    // Helper to get params from the browser URL (e.g., ?redirect=teacher)
    const getRedirectParam = () => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            return params.get('redirect');
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setLoading(true);

        try {
            const res = await apiRequest<UserInfo>('POST', 'auth/login', {
                Username: username,
                Password: password,
            });

            // Trigger MasterProvider update
            login(res.data);
            localStorage.removeItem('hasLoggedOut');

            setSuccess(true);
        } catch (err: unknown) {
            setLoading(false);
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Login failed');
            }
        }
    };

    const getCookie = (name: string): string | null => {
        const match = document.cookie.split('; ').find((row) => row.startsWith(name + '='));
        return match ? decodeURIComponent(match.split('=')[1]) : null;
    };

    useEffect(() => {
        const cleverError = getCookie('cleverLoginError');

        if (cleverError) {
            setError(cleverError);
            document.cookie = 'cleverLoginError=; Max-Age=0; path=/; Secure; SameSite=None';
        }
    }, []);

    useEffect(() => {
        const classLinkError = getCookie('classLinkLoginError');

        if (classLinkError) {
            setError(classLinkError);
            document.cookie = 'classLinkLoginError=; Max-Age=0; path=/; Secure; SameSite=None';
        }
    }, []);

    // --- BUTTON HANDLER LOGIC ---
    const handleCleverLogin = () => {
        let url = `${Constants.API_BASE_URL}auth/clever/login`;
        const redirectParam = getRedirectParam();

        if (redirectParam) {
            url += `?redirect=${encodeURIComponent(redirectParam)}`;
        }

        window.location.href = url;
    };

    const handleClassLinkLogin = () => {
        let url = `${Constants.API_BASE_URL}auth/classlink/login`;

        const redirectParam = getRedirectParam();
        if (redirectParam) {
            url += `?redirect=${encodeURIComponent(redirectParam)}`;
        }

        window.location.href = url;
    };

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <div className="login-wrapper">
                <label className="login-label">
                    Username:
                    <input
                        type="text"
                        className="login-input"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                </label>
                <label className="login-label">
                    Password:
                    <input
                        type="password"
                        className="login-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </label>
                <button
                    type="submit"
                    style={{
                        padding: '12px 20px',
                        borderRadius: 6,
                        background: 'var(--pm-accent)',
                        color: '#000',
                        border: 'none',
                        fontSize: 16,
                        marginTop: 8,
                    }}
                    disabled={loading}
                >
                    {loading ? 'Loading...' : 'Login'}
                </button>
                <hr
                    style={{
                        width: '90%',
                        border: 'none',
                        height: '1px',
                        backgroundColor: '#ccc',
                        margin: '16px 0',
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        width: '90%',
                        maxWidth: 320,
                    }}
                >
                    <button
                        type="button"
                        style={{
                            borderRadius: 6,
                            background: '#00c6dcff',
                            color: '#fff',
                            border: 'none',
                            fontSize: 16,
                            flex: 1,
                        }}
                        onClick={handleClassLinkLogin}
                    >
                        ClassLink Login
                    </button>
                    <button
                        type="button"
                        style={{
                            backgroundColor: '#2b80ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            flex: 1,
                        }}
                        onClick={handleCleverLogin}
                    >
                        Clever Login
                    </button>
                </div>

                {error && <div style={{ color: 'red', marginTop: 16 }}>{error}</div>}
                {success && <div style={{ color: 'green', marginTop: 16 }}>Login successful!</div>}
            </div>
        </form>
    );
};

export default LoginForm;
